import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";

const BOX_SIZE = 440;
const EXPORT_SIZE = 500;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const WHEEL_ZOOM_STEP = 0.1;

const ProfilePhotoCropper = ({
  show,
  onHide,
  onSave,
  onDelete,
  initialImage,
  deleting = false,
  saving = false,
}) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [imgEl, setImgEl] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);

  const lastPos = useRef({ x: 0, y: 0 });

  /* ---------------- RESET ---------------- */
  useEffect(() => {
    if (show) {
      setImageSrc(initialImage || null);
      setZoom(1);
      setOffset({ x: 0, y: 0 });
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => (document.body.style.overflow = "");
  }, [initialImage, show]);

  /* ---------------- CLAMP IMAGE ---------------- */
  const clampOffset = (x, y, z = zoom) => {
    if (!imgEl) return { x, y };

    const scaledW = BOX_SIZE * z;
    const scaledH = BOX_SIZE * z;

    const maxX = (scaledW - BOX_SIZE) / 2;
    const maxY = (scaledH - BOX_SIZE) / 2;

    return {
      x: Math.max(-maxX, Math.min(maxX, x)),
      y: Math.max(-maxY, Math.min(maxY, y)),
    };
  };

  /* ---------------- DRAG ---------------- */
  const onPointerDown = (e) => {
    if (!imageSrc) return;
    setDragging(true);
    lastPos.current = { x: e.clientX, y: e.clientY };
  };

  const onPointerMove = (e) => {
    if (!dragging) return;
    const dx = e.clientX - lastPos.current.x;
    const dy = e.clientY - lastPos.current.y;
    lastPos.current = { x: e.clientX, y: e.clientY };

    setOffset((prev) => clampOffset(prev.x + dx, prev.y + dy));
  };

  const stopDrag = () => setDragging(false);

  /* ---------------- ZOOM ---------------- */
  const onWheelZoom = (e) => {
    if (!imageSrc) return;
    e.preventDefault();
    e.stopPropagation();

    setZoom((z) => {
      const next = Math.min(
        MAX_ZOOM,
        Math.max(MIN_ZOOM, z + (e.deltaY < 0 ? WHEEL_ZOOM_STEP : -WHEEL_ZOOM_STEP))
      );
      setOffset((o) => clampOffset(o.x, o.y, next));
      return next;
    });
  };

  /* ---------------- EXPORT ---------------- */
  const cropAndSave = () => {
    if (!imgEl) return;

    const canvas = document.createElement("canvas");
    canvas.width = EXPORT_SIZE;
    canvas.height = EXPORT_SIZE;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const scaledW = EXPORT_SIZE * zoom;
    const scaledH = EXPORT_SIZE * zoom;

    ctx.save();
    ctx.beginPath();
    ctx.arc(
      EXPORT_SIZE / 2,
      EXPORT_SIZE / 2,
      EXPORT_SIZE / 2,
      0,
      Math.PI * 2
    );
    ctx.clip();

    ctx.drawImage(
      imgEl,
      EXPORT_SIZE / 2 - scaledW / 2 + offset.x,
      EXPORT_SIZE / 2 - scaledH / 2 + offset.y,
      scaledW,
      scaledH
    );

    ctx.restore();

    canvas.toBlob(
      (blob) =>
        blob &&
        onSave(
          new File([blob], "profile.png", { type: "image/png" })
        ),
      "image/png",
      0.98
    );
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" keyboard={false}>
      <Modal.Header closeButton>
        <Modal.Title>Crop Image</Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4">
        {/* CROP AREA */}
        <div
          onMouseDown={onPointerDown}
          onMouseMove={onPointerMove}
          onMouseUp={stopDrag}
          onMouseLeave={stopDrag}
          onWheel={onWheelZoom}
          className="croperimage-box"
          style={{
            width: BOX_SIZE,
            height: BOX_SIZE,
            margin: "0 auto",
            position: "relative",
            overflow: "hidden",
            borderRadius: 8,
            border: "1px solid #ddd",
            background: "#f5f5f5",
            cursor: dragging ? "grabbing" : "grab",
          }}
        >
          {imageSrc && (
            <img
              src={imageSrc}
              ref={setImgEl}
              alt="crop"
              draggable={false}
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transform: `
                  translate(-50%, -50%)
                  translate(${offset.x}px, ${offset.y}px)
                  scale(${zoom})
                `,
                transformOrigin: "center",
                userSelect: "none",
              }}
            />
          )}

          {/* FIXED CIRCLE */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
              pointerEvents: "none",
            }}
          />
        </div>

        {/* ZOOM */}
        <Row className="mt-3 align-items-center">
          <Col sm="auto">
            <Form.Label className="mb-0">Zoom</Form.Label>
          </Col>
          <Col>
            <Form.Range
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={0.05}
              value={zoom}
              disabled={!imageSrc}
              onChange={(e) => {
                const z = Number(e.target.value);
                setZoom(z);
                setOffset((o) => clampOffset(o.x, o.y, z));
              }}
            />
          </Col>
        </Row>

        {/* ACTIONS */}
        <Row className="pt-3 g-2">
          {onDelete && (
            <Col>
              <Button
                variant="outline-danger"
                onClick={onDelete}
                disabled={deleting}
                className="w-100"
              >
                {deleting ? "Removing..." : "Remove Photo"}
              </Button>
            </Col>
          )}
          <Col>
            <Button
              className="btn btn-orange-lg w-100"
              onClick={cropAndSave}
              disabled={!imageSrc || saving}
            >
              {saving ? "Saving..." : "Crop and Save"}
            </Button>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
};

export default ProfilePhotoCropper;
